import React, { useState, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, ListPlus, X, Plus, Pencil, FilePlus2, FileEdit } from 'lucide-react';
import {
  SmartTitleField, SmartNotesField, SmartWorkAreaField, SmartDateField,
  SmartPriorityField, SmartEffortField, SmartAssigneeField,
} from './SmartField';

const API = '/api';


// ---------------------------------------------------------------------------
// Mode detection + input normalisation
// ---------------------------------------------------------------------------

/** Return a `{ mode, rows, entityLabel }` tuple derived from the pending action.
 *  - bulk_create: rows[] from input_data.tasks
 *  - create:       single row wrapping input_data itself
 *  - update:       single row with task_id preserved
 */
function normaliseFromPending(pending) {
  const id = pending.capability_id;
  const input = pending.input_data || {};
  if (id === 'orka.tasks.bulk_create') {
    return {
      mode: 'bulk',
      rows: (input.tasks || []).map((t, i) => rowFromInput(t, i)),
      entityLabel: 'task',
      icon: ListPlus,
      confirmVerb: 'Create',
    };
  }
  if (id === 'orka.tasks.create') {
    return {
      mode: 'single-create',
      rows: [rowFromInput(input, 0)],
      entityLabel: 'task',
      icon: FilePlus2,
      confirmVerb: 'Create',
    };
  }
  if (id === 'orka.tasks.update') {
    return {
      mode: 'single-update',
      rows: [rowFromInput(input, 0)],
      entityLabel: 'task update',
      icon: FileEdit,
      confirmVerb: 'Apply',
    };
  }
  // Unknown — treat like single-create for resiliency
  return {
    mode: 'single-create',
    rows: [rowFromInput(input, 0)],
    entityLabel: 'task',
    icon: FilePlus2,
    confirmVerb: 'Create',
  };
}

function rowFromInput(t, idx) {
  return {
    _key: `${idx}-${Math.random().toString(36).slice(2, 6)}`,
    _selected: true,
    _task_id: t.task_id || null,  // preserved for update mode
    task_title: t.task_title || '',
    task_description: t.task_description || '',
    task_priority: t.task_priority ?? 3,
    task_estimated_effort: t.task_estimated_effort ?? 1,
    task_delivery_date: t.task_delivery_date || '',
    work_area_id: t.work_area_id || '',
    responsible_person_id: t.responsible_person_id || '',
  };
}

/** Collapse a row back down to an API payload (drop empty optional fields). */
function cleanRow(r, mode) {
  const t = {
    task_title: (r.task_title || '').trim(),
    task_priority: Number(r.task_priority),
    task_estimated_effort: Number(r.task_estimated_effort),
  };
  if (r._task_id) t.task_id = r._task_id;
  if (r.task_delivery_date) t.task_delivery_date = r.task_delivery_date;
  if (r.work_area_id) t.work_area_id = r.work_area_id;
  if (r.task_description) t.task_description = r.task_description;
  if (r.responsible_person_id) t.responsible_person_id = r.responsible_person_id;
  if (mode === 'single-update') {
    // update: only send task_title if the user filled it
    if (!t.task_title) delete t.task_title;
  }
  return t;
}


// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Editable preview for task-creating / task-updating pending actions.
 *
 *   orka.tasks.bulk_create — multi-row table with add/remove rows
 *   orka.tasks.create      — single row (one task being created)
 *   orka.tasks.update      — single row pre-filled from existing task fields
 *
 * Fields:
 *   Title (text)       · Work area (Select from WorkAreaContext)
 *   Due date (Calendar Popover) · Priority (P0-P3) · Effort (E1-E5) · Assignee (org members)
 *
 * Submits with `override_input_data` so the user's edits win over the AI's
 * suggestions.
 */
export const TaskPreviewTable = ({ pending, onConfirmed }) => {
  const { mode, rows: initial, entityLabel, icon: Icon, confirmVerb } = useMemo(
    () => normaliseFromPending(pending),
    [pending]
  );

  const [rows, setRows] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [open, setOpen] = useState(initial.length > 1);   // bulk auto-opens

  const selected = useMemo(() => rows.filter(r => r._selected), [rows]);

  const update = useCallback((key, patch) => setRows(prev =>
    prev.map(r => r._key === key ? { ...r, ...patch } : r)
  ), []);
  const remove = useCallback((key) => setRows(prev => prev.filter(r => r._key !== key)), []);
  const addRow = useCallback(() => setRows(prev => [...prev, rowFromInput({}, Date.now())]), []);

  const submit = useCallback(async () => {
    if (selected.length === 0) { toast.error('Pick at least one row'); return; }
    setBusy(true);
    try {
      const cleaned = selected.map(r => cleanRow(r, mode));
      // Build mode-specific override payload
      let override;
      if (mode === 'bulk') {
        override = { tasks: cleaned };
      } else {
        override = cleaned[0] || {};  // single row → flatten
      }
      const r = await fetch(`${API}/ai/v2/pending-actions/${pending.pending_action_id}/confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmation_token: pending.confirmation_token,
          override_input_data: override,
        }),
      });
      const d = await r.json();
      if (r.ok && d.success) {
        setDone(true);
        setOpen(false);
        const n = mode === 'bulk' ? (d.output?.created_count || cleaned.length) : 1;
        toast.success(mode === 'single-update'
          ? 'Task updated'
          : `Created ${n} task${n !== 1 ? 's' : ''}`);
        onConfirmed?.(pending.pending_action_id, d);
      } else {
        const err = d.error || d;
        toast.error(err.error_code || 'AI-CONFIRM',
          { description: err.message || err.title || 'Confirmation failed' });
      }
    } finally { setBusy(false); }
  }, [selected, pending, onConfirmed, mode]);

  if (done) return (
    <div
      className="mt-2 p-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40 text-[11px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5"
      data-testid="entity-preview-done"
    >
      <CheckCircle2 className="w-3 h-3" /> {
        mode === 'single-update' ? 'Update applied.'
          : `${selected.length} ${entityLabel}${selected.length !== 1 ? 's' : ''} created.`
      }
    </div>
  );

  const summaryTitle = mode === 'bulk'
    ? `Bulk create — ${rows.length} ${entityLabel}${rows.length !== 1 ? 's' : ''} ready for review`
    : mode === 'single-update'
      ? 'Edit task — review before applying'
      : 'New task — review before creating';

  return (
    <>
      {/* Inline summary card (always visible in chat) */}
      <div
        className="mt-2 rounded-lg border border-violet-300 bg-violet-50/60 dark:border-violet-900 dark:bg-violet-950/30 overflow-hidden"
        data-testid={`task-preview-table-${pending.pending_action_id}`}
      >
        <div className="px-3 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-violet-700 dark:text-violet-300 text-xs font-medium min-w-0">
            <Icon className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{summaryTitle}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {mode === 'bulk' && (
              <Badge variant="secondary" className="text-[10px]">{selected.length} selected</Badge>
            )}
            <Button
              size="sm" variant="outline"
              className="h-7 text-[11px] gap-1"
              onClick={() => setOpen(true)}
              data-testid={`task-preview-open-editor-${pending.pending_action_id}`}
            >
              <Pencil className="w-3 h-3" /> {mode === 'bulk' ? 'Open editor' : 'Review & edit'}
            </Button>
          </div>
        </div>

        {/* For single-row modes, show a compact inline form right in chat so the
            user doesn't have to open a dialog for trivial edits. */}
        {mode !== 'bulk' && rows[0] && (
          <div className="px-3 pb-3 space-y-2" data-testid={`task-preview-inline-${pending.pending_action_id}`}>
            <SmartTitleField
              value={rows[0].task_title}
              onChange={(v) => update(rows[0]._key, { task_title: v })}
              testId={`task-preview-title-input-${rows[0]._key}`}
              placeholder="Task title"
            />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SmartWorkAreaField
                value={rows[0].work_area_id}
                onChange={(v) => update(rows[0]._key, { work_area_id: v })}
                testId={`task-preview-work-area-${rows[0]._key}`}
              />
              <SmartDateField
                value={rows[0].task_delivery_date}
                onChange={(v) => update(rows[0]._key, { task_delivery_date: v })}
                testId={`task-preview-date-${rows[0]._key}`}
              />
              <SmartPriorityField
                value={rows[0].task_priority}
                onChange={(v) => update(rows[0]._key, { task_priority: v })}
                testId={`task-preview-priority-${rows[0]._key}`}
              />
              <SmartEffortField
                value={rows[0].task_estimated_effort}
                onChange={(v) => update(rows[0]._key, { task_estimated_effort: v })}
                testId={`task-preview-effort-${rows[0]._key}`}
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                size="sm" className="h-7 text-[11px] bg-violet-600 hover:bg-violet-700 gap-1"
                onClick={submit}
                disabled={busy || !(rows[0].task_title || '').trim()}
                data-testid={`task-preview-confirm-${pending.pending_action_id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {confirmVerb}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Full editor modal — used for bulk (always) or single "Review & edit" click */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-5xl w-[95vw] max-h-[90vh] flex flex-col p-0 overflow-hidden"
          data-testid={`task-preview-modal-${pending.pending_action_id}`}
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Icon className="w-5 h-5 text-violet-500" />
              {mode === 'bulk' ? 'Review & confirm bulk task create'
                : mode === 'single-update' ? 'Review & apply task update'
                  : 'Review & create task'}
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Orka pre-filled best-guess values. Edit anything below — categories come from your
              org's canonical list, assignees from your team, and the due date uses the same
              picker as the rest of the app. Nothing is saved until you hit
              <strong> {confirmVerb} &nbsp;{mode === 'bulk' ? '& create' : ''}</strong>.
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-auto px-2 py-2">
            <Table className="text-xs">
              <TableHeader>
                <TableRow>
                  {mode === 'bulk' && <TableHead className="w-8"></TableHead>}
                  <TableHead className="min-w-[220px]">Title</TableHead>
                  <TableHead className="w-[170px]">Category</TableHead>
                  <TableHead className="w-[160px]">Assignee</TableHead>
                  <TableHead className="w-[150px]">Due date</TableHead>
                  <TableHead className="w-[140px]">Priority</TableHead>
                  <TableHead className="w-[150px]">Effort</TableHead>
                  {mode === 'bulk' && <TableHead className="w-8"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(r => (
                  <TableRow
                    key={r._key}
                    className={cn(!r._selected && 'opacity-50')}
                    data-testid={`task-preview-row-${r._key}`}
                  >
                    {mode === 'bulk' && (
                      <TableCell className="p-2">
                        <input
                          type="checkbox"
                          checked={r._selected}
                          onChange={(e) => update(r._key, { _selected: e.target.checked })}
                          data-testid={`task-preview-checkbox-${r._key}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="p-2 align-top">
                      <SmartTitleField
                        value={r.task_title}
                        onChange={(v) => update(r._key, { task_title: v })}
                        placeholder="Task title"
                        testId={`task-preview-title-input-${r._key}`}
                      />
                      <SmartNotesField
                        value={r.task_description}
                        onChange={(v) => update(r._key, { task_description: v })}
                        placeholder="Notes (optional)"
                        className="mt-1"
                        testId={`task-preview-notes-${r._key}`}
                      />
                    </TableCell>
                    <TableCell className="p-2">
                      <SmartWorkAreaField
                        value={r.work_area_id}
                        onChange={(v) => update(r._key, { work_area_id: v })}
                        testId={`task-preview-work-area-${r._key}`}
                      />
                    </TableCell>
                    <TableCell className="p-2">
                      <SmartAssigneeField
                        value={r.responsible_person_id}
                        onChange={(v) => update(r._key, { responsible_person_id: v })}
                        testId={`task-preview-assignee-${r._key}`}
                      />
                    </TableCell>
                    <TableCell className="p-2">
                      <SmartDateField
                        value={r.task_delivery_date}
                        onChange={(v) => update(r._key, { task_delivery_date: v })}
                        testId={`task-preview-date-${r._key}`}
                      />
                    </TableCell>
                    <TableCell className="p-2">
                      <SmartPriorityField
                        value={r.task_priority}
                        onChange={(v) => update(r._key, { task_priority: v })}
                        testId={`task-preview-priority-${r._key}`}
                      />
                    </TableCell>
                    <TableCell className="p-2">
                      <SmartEffortField
                        value={r.task_estimated_effort}
                        onChange={(v) => update(r._key, { task_estimated_effort: v })}
                        testId={`task-preview-effort-${r._key}`}
                      />
                    </TableCell>
                    {mode === 'bulk' && (
                      <TableCell className="p-2">
                        <button
                          type="button"
                          onClick={() => remove(r._key)}
                          className="text-muted-foreground hover:text-red-600 transition"
                          title="Drop row"
                          data-testid={`task-preview-remove-${r._key}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {rows.length === 0 && (
              <div className="p-8 text-center text-xs text-muted-foreground">
                All rows removed. Add a row to continue.
              </div>
            )}

            {mode === 'bulk' && (
              <div className="px-2 pt-2">
                <Button
                  variant="outline" size="sm" className="h-8 text-xs gap-1"
                  onClick={addRow}
                  data-testid={`task-preview-add-row-${pending.pending_action_id}`}
                >
                  <Plus className="w-3 h-3" /> Add row
                </Button>
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t flex items-center justify-between bg-muted/30">
            <div className="text-xs text-muted-foreground">
              {mode === 'bulk'
                ? `${selected.length} of ${rows.length} selected · uncheck to skip · × to drop`
                : mode === 'single-update' ? 'Update the fields you want to change, leave the rest.'
                  : 'All fields are editable before creation.'}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(false)} className="h-8 text-xs">
                Cancel
              </Button>
              <Button
                size="sm" className="h-8 text-xs bg-violet-600 hover:bg-violet-700 gap-1"
                onClick={submit}
                disabled={busy || selected.length === 0}
                data-testid={`task-preview-confirm-modal-${pending.pending_action_id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                {mode === 'bulk'
                  ? `Confirm & create ${selected.length}`
                  : mode === 'single-update' ? 'Apply update'
                    : 'Create task'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TaskPreviewTable;
